import { useEffect, useState } from "react";
import { NextPage, NextPageContext } from "next";
import { useRouter } from "next/router";
import { useLazyQuery } from "@apollo/client";

import { meQuery } from "src/lib/graphql/user.queries";
import { apiUrl, isDev, isProd, restrictedPaths } from "src/lib/environment";
import { sanitizeUrl } from "src/lib/functions";
import identifyUser from "src/lib/identifyUser";

type LoginProps = {
  query: any;
  signup: () => void;
};

const Login: NextPage<LoginProps> = ({ query, signup }) => {
  const router = useRouter();

  const redirect = sanitizeUrl(query.redirect) || "/";

  const [redirecting, setRedirecting] = useState(false);

  const [fetchMe, { data: userData, loading }] = useLazyQuery(meQuery, {
    fetchPolicy: "network-only",
  });

  const onMessage = async (message: any) => {
    if (
      !redirecting &&
      !loading &&
      !userData?.me &&
      (isDev || message.origin === "https://api.hash.ai")
    ) {
      fetchMe();
    }
  };

  useEffect(() => {
    window.addEventListener("message", onMessage, false);

    return () => window.removeEventListener("message", onMessage, false);
  }, []);

  useEffect(() => {
    if (userData?.me) {
      const user = userData.me;
      setRedirecting(true);
      if (isProd) {
        identifyUser(user);
      }
      if (core) {
        window.location.replace(redirect);
      } else if (
        redirect.startsWith("/") &&
        !redirect.includes("//") &&
        !redirect.includes("\\") &&
        !redirect.includes("%")
      ) {
        if (redirect === "/profile") {
          router.replace(`/@${user.shortname}`);
        } else {
          router.replace(redirect);
        }
      } else {
        router.replace("/");
      }
    }
  }, [userData]);

  const core =
    redirect.startsWith("https://core.hash.ai/") ||
    redirect === "https://core.hash.ai" ||
    (isDev &&
      (redirect.startsWith("https://staging.hash.ai/") ||
        redirect.startsWith("http://localhost:8080/") ||
        redirect.startsWith("http://127.0.0.1:8080/")) &&
      !redirect.startsWith("http://localhost.hash.ai"));

  let protectedPage: boolean = false;
  if (core || restrictedPaths.find((path) => redirect.startsWith(path))) {
    protectedPage = true;
  }

  return (
    <div className="login-page black content-min-height">
      {protectedPage && (
        <div className="large-text bold">
          You must be signed in to{" "}
          {core ? "see this simulation" : "view this page"}
        </div>
      )}
      <div className="large-text">
        Please sign in or{" "}
        <a href="#" onClick={() => signup()} className="text-link">
          register
        </a>{" "}
        to {core ? "view or request access to the file" : "proceed"}
      </div>
      <div className="login-window">
        <iframe className="login-frame" src={apiUrl} scrolling="no" />
      </div>
    </div>
  );
};

Login.getInitialProps = async (
  ctx: NextPageContext & {
    query: any;
    signup: () => void;
  }
) => {
  return {
    query: ctx.query,
    signup: ctx.signup,
  };
};

export default Login;
